import * as React from 'react'
import { createPlugin, PluginGridRowAPI } from '@kaminrunde/fireside-utils'
import styled from 'styled-components'
import remapBackgroundRows, { BgRow } from './remapBackgroundRows'

type State = {
  [mediaSize: string]: BgRow[]
}

function Modal (props:PluginGridRowAPI<State>) {
  
  const selectedColor = React.useMemo(() => {
    const rowNumber = props.row +1
    if(props.state[props.mediaSize]) {
      const row = props.state[props.mediaSize].find((row) => row.row === rowNumber)
      if(row) {
        return row.background
      }
    }
    return null
  }, [props.state, props.mediaSize, props.row])

  const toggleColor = (color: string) => {
    const rowNumber = props.row +1
    let currentColor:string | null = null

    if(props.state[props.mediaSize]) {
      const row = props.state[props.mediaSize].find((row) => row.row === rowNumber)
      if(row) {
        currentColor = row.background
      }
    }

    let nextRows:BgRow[] = []

    if(props.state[props.mediaSize]) {
      nextRows = props.state[props.mediaSize].filter((row) => row.row !== rowNumber)
    }

    if(currentColor !== color) {
      nextRows.push({
        background: color,
        row: rowNumber
      })
    }

    props.setState({
      ...props.state,
      [props.mediaSize]: nextRows.sort((a, b) => a.row - b.row)
    })
  }

  const colors = [
    { value: 'rgba(0,0,0,0.08)', label: '' },
    { value: 'rgba(0,70,132,0.08)', label: 'V' },
    { value: 'rgba(0,117,190,0.08)', label: 'EM' },
    { value: 'rgba(255,237,0,0.08)', label: 'P' }
  ]

  return (
    <Wrapper>
      {colors.map((color) => (
        <Color key={color.value} $value={color.value} $selected={selectedColor === color.value} onClick={() => toggleColor(color.value)}>
          {color.label}
        </Color>
      ))}
    </Wrapper>
  )
}

function Badge (props:PluginGridRowAPI<State>) {
  const rowNumber = props.row +1
  if(props.state[props.mediaSize]) {
    const row = props.state[props.mediaSize].find((row) => row.row === rowNumber)
    if(row) {
      return <div style={{ backgroundColor: row.background, width: 20, height: 20, borderRadius: '50%' }} />
    }
  }
  return null
}

const Wrapper = styled.div`
  margin-top: 20px;
  display: flex;
  gap: 20px;
  padding-left: 5px;
`

const Color = styled.div<{ $value: string, $selected: boolean }>`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${(props) => props.$selected ? 'black' : 'rgb(70, 130, 180)'};
  background-color: ${(props) => props.$value};
  cursor: pointer;

  ${(props) => props.$selected && `
    outline: 5px solid rgb(70, 130, 180);
  `}
`

export default createPlugin<State, { key: string }>((ctx) => {

  ctx.extendGridRow({
    settingsModal: {
      title: 'Background Color',
      component: Modal
    },
    badge: {
      component: Badge,
      isActive: (props) => {
        if(!props.state[props.mediaSize]) return false
        return !!props.state[props.mediaSize].find((row) => row.row === props.row +1)
      }
    }
  })

  // WDV-5206: colors are stored per row index. When modules are added or
  // removed the rows shift, so we re-anchor every color to the leftmost
  // component of its row whenever the grid structure changes.
  //
  // api.prevStory is the story last confirmed by the connector round-trip and
  // can lag several update cycles behind (in dev it never updates at all).
  // Remapping against a stale grid re-applies the same move on every debounced
  // run and cycles the colors endlessly, so the comparison baseline is tracked
  // here in the plugin instead — prevStory only seeds the very first run.
  let baselineGrids: Record<string, string[][] | undefined> | null = null

  ctx.onStoryUpdate((api) => {
    const currentGrids: Record<string, string[][] | undefined> = {}
    for (const mediaSize in api.story.grids) {
      currentGrids[mediaSize] = api.story.grids[mediaSize]?.grid
    }

    let prevGrids = baselineGrids
    if (!prevGrids && api.prevStory) {
      prevGrids = {}
      for (const mediaSize in api.prevStory.grids) {
        prevGrids[mediaSize] = api.prevStory.grids[mediaSize]?.grid
      }
    }
    baselineGrids = currentGrids
    if (!prevGrids) return

    const state = api.state || {}
    const nextState: State = {}
    let changed = false

    for (const mediaSize in state) {
      const rows = state[mediaSize] || []
      const prevGrid = prevGrids[mediaSize]
      const nextGrid = currentGrids[mediaSize]

      if (!prevGrid || !nextGrid) {
        if (rows.length) nextState[mediaSize] = rows
        continue
      }

      const remapped = remapBackgroundRows(rows, prevGrid, nextGrid)
      if (JSON.stringify(remapped) !== JSON.stringify(rows)) changed = true
      if (remapped.length) nextState[mediaSize] = remapped
    }

    // only write when something moved, otherwise the SET_STATE dispatched by
    // the app would re-trigger the update cycle forever
    if (changed) api.setState(nextState)
  })

  return {}
})
